import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { postData } from '../utility/Utility';
import { toast } from 'react-toastify';
import '../App.css';
import CustomLoader from './CustomLoader/CustomLoader';

function WelcomeDashboard() {
    const [userData, setUserData] = useState({});
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const fetchProfileData = async () => {
        try {
            const response = await postData('api/admin/profile');
            if (response?.success) {
                setUserData({
                    ...response.data,
                    role: response.data?.role,
                    permission: response.permission?.permission || []
                });
            }
        } catch (error) {
            console.error('Error fetching profile data:', error);
            toast.error('Error loading user profile');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProfileData();
    }, []);

    const getWelcomeMessage = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good Morning';
        if (hour < 17) return 'Good Afternoon';
        return 'Good Evening';
    };

    const getUserName = () => {
        const name = userData?.name || userData?.fullName || userData?.email?.split('@')[0] || 'User';
        return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    };

    const getRoleBasedMessage = () => {
        const role = userData?.role;
        switch (role) {
            case 'super-admin':
                return 'Super Administrator';
            case 'admin':
                return 'Administrator';
            case 'employee':
                return 'Employee';
            case 'agent':
                return 'Agent';
            default:
                return 'Team Member';
        }
    };

    const getPermissionBasedCards = () => {
        if (!userData.permission || !Array.isArray(userData.permission)) {
            // Return welcome card even without permissions for authenticated users
            return [{
                title: 'Welcome Dashboard',
                description: 'View your personalized dashboard',
                icon: '🏠',
                color: '#1f5091',
                link: '/welcome',
                permission: 'welcomeDashboard'
            }];
        }

        const permissionNames = userData.permission.map(p => p.permissionName);

        const cards = [];

        // Welcome Dashboard - always show for authenticated users
        cards.push({
            title: 'Welcome Dashboard',
            description: 'View your personalized dashboard',
            icon: '🏠',
            color: '#1f5091',
            link: '/welcome',
            permission: 'welcomeDashboard'
        });

        // Dashboard card - only show if they have dashboard permission
        if (permissionNames.includes('admindashboard')) {
            cards.push({
                title: 'Analytics Dashboard',
                description: 'View comprehensive analytics and statistics',
                icon: '📊',
                color: '#667eea',
                link: '/dashboard',
                permission: 'admindashboard'
            });
        }

        // User Management
        if (permissionNames.includes('userManagement')) {
            cards.push({
                title: 'User Management',
                description: 'Manage users and their accounts',
                icon: '👥',
                color: '#667eea',
                link: '/user-management',
                permission: 'userManagement'
            });
        }

        // Employee Management
        if (permissionNames.includes('EmployeeManagement')) {
            cards.push({
                title: 'Employee Management',
                description: 'Manage employees and their roles',
                icon: '👔',
                color: '#4facfe',
                link: '/employee-list',
                permission: 'EmployeeManagement'
            });
        }

        // Inquiries Management
        const inquiryPermissions = ['websitesInquiryFrom', 'websitesHomePageInquiry', 'websitesContactInquiry'];
        if (permissionNames.some(p => inquiryPermissions.includes(p))) {
            cards.push({
                title: 'Inquiries Management',
                description: 'Handle customer inquiries and leads',
                icon: '📞',
                color: '#f093fb',
                link: '/inquiries-management',
                permission: 'websitesInquiryFrom'
            });
        }

        // Insurance Management
        const insurancePermissions = ['CategoryManagement', 'SubCategoryManagement'];
        if (permissionNames.some(p => insurancePermissions.includes(p))) {
            cards.push({
                title: 'Insurance Management',
                description: 'Manage insurance categories and policies',
                icon: '📋',
                color: '#43e97b',
                link: '/category-list',
                permission: 'CategoryManagement'
            });
        }

        // Commission Management
        if (permissionNames.includes('commissionManagement')) {
            cards.push({
                title: 'Commission Management',
                description: 'Manage commission rules and payments',
                icon: '💰',
                color: '#fa709a',
                link: '/commission-lists',
                permission: 'commissionManagement'
            });
        }

        // Role Management
        if (permissionNames.includes('roleManagement')) {
            cards.push({
                title: 'Role Management',
                description: 'Manage user roles and permissions',
                icon: '🔐',
                color: '#30cfd0',
                link: '/role-list',
                permission: 'roleManagement'
            });
        }

        // Reward Points Management
        if (permissionNames.includes('RewardPointsManagement')) {
            cards.push({
                title: 'Policy Reward List',
                description: 'View and manage policy reward points',
                icon: '⭐',
                color: '#ffd700',
                link: '/reward-points',
                permission: 'RewardPointsManagement'
            });
        }

        // Notification Management
        if (permissionNames.includes('NotificationManagement')) {
            cards.push({
                title: 'Notifications',
                description: 'Send and manage notifications',
                icon: '🔔',
                color: '#ff6b6b',
                link: '/notification-list',
                permission: 'NotificationManagement'
            });
        }

        return cards.slice(0, 6); // Limit to 6 cards for better layout
    };

    const handleCardClick = (link) => {
        navigate(link);
    };

    if (loading) {
        return <CustomLoader />;
    }

    const permissionCards = getPermissionBasedCards();

    return (
        <div className="content-page">
            <div className="content">
                <div className="container-fluid px-3 px-lg-4">
                    {/* Welcome Header */}
                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="card border-0 shadow-sm welcome-header">
                                <div className="welcome-header-pattern"></div>
                                <div className="card-body welcome-header-content">
                                    <div className="row align-items-center">
                                        <div className="col-lg-8">
                                            <div className="mb-3">
                                                <span className="welcome-badge">
                                                    🎯 Welcome Back
                                                </span>
                                            </div>
                                            <h1 className="welcome-title">
                                                {getWelcomeMessage()}, <span className="user-name">{getUserName()}</span>! 👋
                                            </h1>
                                            <p className="welcome-subtitle">
                                                Welcome to the Insurance Admin Portal. You are logged in as
                                                <span className="role-badge mx-2">{getRoleBasedMessage()}</span>
                                            </p>
                                            <div className="session-info">
                                                <div className="session-indicator">
                                                    <div className="session-dot"></div>
                                                    <small className="session-text">Active Session</small>
                                                </div>
                                                <div className="date-info">
                                                    <i className="mdi mdi-clock date-icon"></i>
                                                    <small className="session-text">
                                                        {new Date().toLocaleDateString()}
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-lg-4 text-lg-end mt-4 mt-lg-0">
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Quick Actions */}
                    {permissionCards.length > 0 && (
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="quick-actions-header">
                                    <div>
                                        <h3 className="quick-actions-title">
                                            🚀 Quick Actions
                                        </h3>
                                        <p className="quick-actions-subtitle">Access your frequently used features based on your permissions</p>
                                    </div>
                                    <div className="modules-status">
                                        <span className="status-dot"></span>
                                        <small className="modules-count">{permissionCards.length} modules available</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="row g-3">
                        {permissionCards.map((card) => (
                            <div key={card.permission} className="col-12 col-sm-6 col-lg-4">
                                <div
                                    className="card border-0 shadow-sm h-100 action-card"
                                    onClick={() => handleCardClick(card.link)}
                                >
                                    <div className="card-top-border" style={{ borderColor: card.color }}></div>
                                    <div className="card-body p-4">
                                        <div className="d-flex align-items-start">
                                            <div
                                                className="action-icon me-3"
                                                style={{
                                                    backgroundColor: `${card.color}15`,
                                                    color: card.color,
                                                    borderColor: `${card.color}30`
                                                }}
                                            >
                                                {card.icon}
                                            </div>
                                            <div className="flex-grow-1">
                                                <h5 className="action-title">{card.title}</h5>
                                                <p className="action-description">{card.description}</p>
                                                <div className="d-flex align-items-center">
                                                    <span className="action-link" style={{ color: card.color }}>
                                                        Access Now →
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* No Permissions Message */}
                    {permissionCards.length === 0 && (
                        <div className="row">
                            <div className="col-12">
                                <div className="card border-0 shadow-sm no-permissions-card">
                                    <div className="no-permissions-icon">
                                        🔒
                                    </div>
                                    <h4 className="no-permissions-title">
                                        No Permissions Available
                                    </h4>
                                    <p className="no-permissions-text">
                                        You don't have any permissions assigned yet. Please contact your administrator to get the necessary permissions.
                                    </p>
                                    <button
                                        className="btn btn-primary mt-3"
                                        onClick={() => navigate('/admin-profile')}
                                    >
                                        <i className="mdi mdi-account me-2"></i>
                                        View Profile
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Help Section */}
                    <div className="row mt-5">
                        <div className="col-12">
                            <div className="card border-0 help-card">
                                <div className="card-body p-4">
                                    <div className="row align-items-center">
                                        <div className="col-md-8">
                                            <h5 className="help-title">
                                                💡 Need Help?
                                            </h5>
                                            <p className="help-text">
                                                If you need assistance or have questions, please contact your administrator or check the help documentation.
                                            </p>
                                        </div>
                                        <div className="col-md-4 text-md-end mt-3 mt-md-0">
                                            <button className="btn btn-outline-primary me-2">
                                                <i className="mdi mdi-help-circle me-1"></i>
                                                Help Center
                                            </button>
                                            <button className="btn btn-primary" onClick={() => navigate('/admin-profile')}>
                                                <i className="mdi mdi-account me-1"></i>
                                                My Profile
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div >
    );
}

export default WelcomeDashboard;
